import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ChecklistStatus = 'BOM' | 'RUIM' | 'NAO_EXISTENTE';

export type ChecklistAcessoriosState = {
  items: ChecklistItem[];
  observacao: string;
};

type VehicleTipo = "carro" | "moto";

export type ChecklistItem = {
  key: string;
  label: string;
  status: ChecklistStatus | null;
  tipo: VehicleTipo;
};

/**
 * 🔴 **Nasce VAZIO desde a at-64 — e isso corrige um defeito que apagava dado.**
 *
 * Aqui havia **46 entradas que são legendas de FOTO** (as mesmas de
 * `utils/vistoriaLegendas.ts`, carro + moto), e não acessórios. Elas **nunca**
 * chegavam à tela: o `useEffect` do checklist substituía a lista pelos 23
 * acessórios no primeiro render. Era dado morto — e pior que inútil.
 *
 * O estrago aparecia ao retomar uma vistoria pela `/carteira`:
 * `hidratarFluxoVistoria` reseta este slice, e quando o próximo passo era LAUDO a
 * tela do laudo abria direto, **sem** passar pelo checklist. Ela lia estes 46
 * itens com `status: null` e os enviava no `PATCH accessories` ao finalizar — o
 * backend substitui o campo inteiro, então as 23 respostas salvas eram apagadas,
 * com a vistoria já concluída.
 *
 * A correção tem duas metades: o laudo passou a mandar **só `pdf_image_url`** no
 * PATCH de finalização (e a exibir o checklist do banco), e esta lista virou o que
 * sempre deveria ter sido — vazia, populada pela tela de checklist a partir de
 * `utils/vistoriaAcessorios.ts`.
 *
 * ⚠️ Navegadores com `persist:root` antigo ainda trazem as 46 na reidratação; o
 * `useEffect` da tela as substitui pelos 23, como já fazia.
 */
const initialItems: ChecklistItem[] = [];

const initialState: ChecklistAcessoriosState = {
  items: initialItems,
  observacao: '',
};

type SetItemStatusPayload = {
  key: string;
  status: ChecklistStatus;
};

const checklistAcessoriosSlice = createSlice({
  name: 'checklistAcessorios',
  initialState,
  reducers: {
    setItems(state, action: PayloadAction<ChecklistItem[]>) {
      state.items = action.payload;
    },
    setItemStatus(state, action: PayloadAction<SetItemStatusPayload>) {
      const { key, status } = action.payload;
      const item = state.items.find((i) => i.key === key);
      if (item) item.status = status;
    },
    setObservacao(state, action: PayloadAction<string>) {
      state.observacao = action.payload;
    },
    resetChecklistAcessorios() {
      return initialState;
    },
  },
});

export const { setItems, setItemStatus, setObservacao, resetChecklistAcessorios } =
  checklistAcessoriosSlice.actions;

export const selectChecklistAcessorios = (state: any): ChecklistAcessoriosState => state.checklistAcessorios;
export const selectChecklistItems = (state: any): ChecklistItem[] => state.checklistAcessorios.items;
export const selectChecklistObservacao = (state: any): string => state.checklistAcessorios.observacao;

export default checklistAcessoriosSlice.reducer;
